import { Autocomplete, Chip, CircularProgress, Stack, TextField } from '@mui/material'

import useTeammateSearch from '@/hooks/useTeammateSearch'
import { isListboxNearBottom } from '@/utils/listboxScroll'

export interface ResourceOption {
  id: string
  label: string
}

export interface FineGrainedResourcePickerProps {
  selectedTeammates: ResourceOption[]
  onTeammatesChange: (options: ResourceOption[]) => void
  disabled?: boolean
}

/**
 * Picks the Teammates a fine-grained API key may reach. Uses the same debounced, server-filtered
 * search the Teams builder uses for its Teammate picker, rather than loading every Teammate up
 * front, and pages in further Teammates as the list is scrolled so it never stops at the first
 * page. (WF-2657)
 */
export default function FineGrainedResourcePicker({ selectedTeammates, onTeammatesChange, disabled }: FineGrainedResourcePickerProps) {
  const { setSearchInput, debouncedSearch, results, isSearching, isLoadingMore, loadMore, resetSearch } = useTeammateSearch()

  // Keep already-selected options in the list even when they fall outside the current search, so
  // selecting a second Teammate never silently drops the first.
  const options: ResourceOption[] = [
    ...selectedTeammates,
    ...results.filter(result => !selectedTeammates.some(selected => selected.id === result.id)).map(r => ({ id: r.id, label: r.label })),
  ]

  return (
    <Stack spacing={2}>
      <Autocomplete
        multiple
        size="small"
        id="api-key-teammates-autocomplete"
        openOnFocus
        disablePortal
        disabled={disabled}
        value={selectedTeammates}
        onChange={(_, value) => onTeammatesChange(value)}
        onClose={() => resetSearch()}
        onInputChange={(_, value, reason) => {
          if (reason === 'input') setSearchInput(value)
        }}
        options={options}
        getOptionLabel={option => option.label ?? ''}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        filterOptions={x => x}
        loading={isSearching || isLoadingMore}
        ListboxProps={{
          onScroll: event => {
            if (isListboxNearBottom(event.currentTarget)) loadMore()
          },
        }}
        noOptionsText={isSearching ? 'Searching…' : debouncedSearch ? 'No matching teammates' : 'No teammates available'}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => <Chip {...getTagProps({ index })} key={option.id} size="small" label={option.label} />)
        }
        renderInput={params => (
          <TextField
            {...params}
            label="Teammates"
            placeholder="Search teammates"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isSearching || isLoadingMore ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Stack>
  )
}
